import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import StatusChip from "../components/StatusChip";
import { RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";

export default function Diagnostics() {
    const [health, setHealth] = useState(null);
    const [diag, setDiag] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [h, d] = await Promise.all([api.health(), api.katlasDiagnostics()]);
            setHealth(h);
            setDiag(d);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <div data-testid="diagnostics-page">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="label-eyebrow mb-3">Diagnostics</p>
                    <h1 className="font-serif text-5xl tracking-tight text-ink">
                        Runtime governance health
                    </h1>
                    <p className="mt-4 text-base text-ink-secondary max-w-3xl">
                        Redacted view only. No bearer token, Authorization header, cert_text, cert_encoded or signed
                        envelope is shown here. Stage 1 = key-name propagation. Stage 2 = secret-value activation.
                    </p>
                </div>
                <button className="btn-ghost" onClick={load} disabled={loading} data-testid="diag-refresh-btn">
                    <RefreshCw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-12 gap-6 lg:gap-8">
                <div className="col-span-12 lg:col-span-6">
                    <div className="panel p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <ShieldCheck size={16} strokeWidth={1.5} className="text-forest" />
                            <span className="label-eyebrow">/api/health</span>
                        </div>
                        <pre
                            className="font-mono text-xs text-slate2 bg-[#FBFAF6] border border-parchment-rule p-4 overflow-auto"
                            data-testid="health-payload"
                        >
                            {JSON.stringify(health, null, 2)}
                        </pre>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-6">
                    <div className="panel p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={16} strokeWidth={1.5} className="text-forest" />
                                <span className="label-eyebrow">/api/katlas/status</span>
                            </div>
                            <StatusChip
                                status={diag?.status}
                                label={diag?.label}
                                testId="diag-katlas-status-chip"
                            />
                        </div>
                        <dl className="text-sm space-y-2">
                            <Row k="Mode" v={diag?.mode} />
                            <Row k="Node URL present" v={String(!!diag?.node_url_present)} />
                            <Row
                                k="katlas_bearer_token_present"
                                v={String(!!diag?.katlas_bearer_token_present)}
                                testId="diag-bearer-present"
                            />
                            <Row
                                k="token_source_used"
                                v={diag?.token_source_used}
                                testId="diag-token-source"
                            />
                            <Row k="HTTP status" v={diag?.http_status ?? "—"} />
                            <Row k="Reason" v={diag?.reason ?? "—"} />
                        </dl>
                    </div>
                </div>

                <div className="col-span-12">
                    <div className="panel p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <AlertTriangle size={16} strokeWidth={1.5} className="text-ochre" />
                            <span className="label-eyebrow">Runtime visibility — env key names only</span>
                        </div>
                        <div className="panel divide-y divide-parchment-rule" data-testid="env-visibility-table">
                            <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-[#F2EFE9]">
                                <div className="col-span-4 label-eyebrow">Key</div>
                                <div className="col-span-2 label-eyebrow">Present</div>
                                <div className="col-span-2 label-eyebrow">Value length</div>
                                <div className="col-span-2 label-eyebrow">Non-empty</div>
                                <div className="col-span-2 label-eyebrow text-right">Notes</div>
                            </div>
                            {(diag?.runtime_visibility?.matching_env_keys || []).map((k) => (
                                <div
                                    key={k.name}
                                    className="grid grid-cols-12 gap-3 px-5 py-3"
                                    data-testid={`env-row-${k.name}`}
                                >
                                    <div className="col-span-4 data-cell text-xs">{k.name}</div>
                                    <div className="col-span-2 data-cell text-xs">{String(k.present)}</div>
                                    <div className="col-span-2 data-cell text-xs">{k.value_length}</div>
                                    <div className="col-span-2 data-cell text-xs">{String(k.non_empty)}</div>
                                    <div className="col-span-2 text-right data-cell text-xs text-ink-muted">
                                        {k.name === "KATLAS_BEARER_TOKEN" && !k.non_empty
                                            ? "Stage 1: blank placeholder"
                                            : k.name === "KATLAS_BEARER_TOKEN" && k.non_empty
                                            ? "Stage 2: live"
                                            : "—"}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {(diag?.runtime_visibility?.forbidden_aliases_seen || []).length > 0 && (
                            <p className="mt-4 text-xs text-oxblood font-mono uppercase tracking-[0.18em]">
                                Forbidden aliases detected in env (will be ignored):{" "}
                                {diag.runtime_visibility.forbidden_aliases_seen.join(", ")}
                            </p>
                        )}
                    </div>
                </div>

                <div className="col-span-12">
                    <div className="panel-sunken p-6">
                        <p className="label-eyebrow mb-3">Stage discipline</p>
                        <ul className="text-sm space-y-2">
                            <li>
                                <span className="data-cell text-xs">Stage 1</span> — Required backend env names are
                                visible to the runtime, even if blank. Diagnostics show{" "}
                                <span className="data-cell text-xs">KATLAS_BEARER_TOKEN</span> with{" "}
                                <span className="data-cell text-xs">value_length: 0, non_empty: false</span>. Status:
                                unconfigured (acceptable).
                            </li>
                            <li>
                                <span className="data-cell text-xs">Stage 2</span> — Real secret value injected
                                securely at runtime into the production backend. Diagnostics show{" "}
                                <span className="data-cell text-xs">katlas_bearer_token_present: true</span> and{" "}
                                <span className="data-cell text-xs">token_source_used: KATLAS_BEARER_TOKEN</span>.
                                Status: active.
                            </li>
                            <li>
                                If the token value is wrong, status will be{" "}
                                <span className="data-cell text-xs">auth_rejected</span> — never{" "}
                                <span className="data-cell text-xs">unreachable</span>.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ k, v, testId }) {
    return (
        <div className="grid grid-cols-12 gap-3 border-b border-parchment-rule pb-2 last:border-b-0">
            <dt className="col-span-5 label-eyebrow self-center">{k}</dt>
            <dd className="col-span-7 data-cell" data-testid={testId}>
                {v === null || v === undefined || v === "" ? "—" : String(v)}
            </dd>
        </div>
    );
}
